/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 UNESP Universidade Estadual Paulista "Júlio de Mesquita Filho"
 * Copyright 2024-2026 UNICAMP Universidade Estadual de Campinas
 *
 */

import { AfterViewInit, Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { UnespCoreMessageService } from 'src/libs/unesp-core'
import { ComissaoNotaProvaInscricaoDTO } from 'src/app/models/ComissaoNotaProvaInscricaoDTO';
import { ComissaoNotaProvaInscricaoRequest } from 'src/app/models/ComissaoNotaProvaInscricaoRequest';
import { MatSort } from '@angular/material/sort'
import { ComissaoJulgadoraService } from '../comissao-julgadora-service'
import { Subject, Subscription } from 'rxjs';
import { EnviarNotasEnvelopeDTO } from 'src/app/models/EnviarNotasEnvelopeDTO';
import { MatDialog } from '@angular/material/dialog';
import { ArquivoViewerAvaliacaoComponent } from '../arquivo-viewer-avaliacao/arquivo-viewer-avaliacao.component';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../confirm-dialog/confirm-dialog.component';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { ReporteIdentificacaoService } from 'src/app/services/reporte-identificacao.service';
import { ReporteHistoricoDialogComponent } from '../reporte-historico-dialog/reporte-historico-dialog.component';

@Component({
  selector: 'inscricoes-lista-nota-examinador-unicamp',
  templateUrl: './lista-nota-examinador.component.html',
  styleUrls: ['./lista-nota-examinador.component.scss'],
  animations: [
    trigger('slideDown', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-10px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ]),
      transition(':leave', [
        animate('300ms ease-in', style({ opacity: 0, transform: 'translateY(-10px)' }))
      ])
    ])
  ]
})
export class ListaNotaExaminadorComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild(MatSort) sort!: MatSort
  idConcurso: number = 0;
  tipoProva: string = "";  
  recursoId: number | null = null;  
  provaPeso: number = 0;

  colunasGrid: string[] = ['inscricao', 'nome', 'nota'];
  dataSource: MatTableDataSource<ComissaoNotaProvaInscricaoDTO> = new MatTableDataSource()

  querySubscription!: Subscription
  paramSubscription!: Subscription

  private readonly notaSubject = new Subject<{ inscricaoId: number; nota: number }>();
  envelope: { id: number; dataEnvio: string; enviadoPor: string } | null = null;
  instrucoesExpandidas: boolean = false;
  envelopeBloqueado: boolean = false;
  reportesVisiveis: any[] = [];
  isPresidente: boolean = false;

  constructor(
    private readonly unespCoreMessageService: UnespCoreMessageService,
    private readonly route: ActivatedRoute,
    private readonly comissaoJulgadorService: ComissaoJulgadoraService,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly reporteIdentificacaoService: ReporteIdentificacaoService
  ) {}

  ngOnInit(): void {
    this.paramSubscription = this.route.params.subscribe(params => {
      this.idConcurso = Number(params['idConcurso']);
      this.tipoProva = params['tipoProva'] || '';
      this.recursoId = params['recursoId'] ? Number(params['recursoId']) : null;
    });

    this.querySubscription = this.route.queryParams.subscribe(params => {
      this.provaPeso = params['peso']
    })

    if (this.tipoProva === 'ESCRITA') {      
      this.colunasGrid.push('nomeDocumentoProva');
    } else {
      this.colunasGrid.push('documentos');
    }
  }

  ngAfterViewInit() {
    this.getData();
    if (this.tipoProva === 'ESCRITA') {
      this.reporteIdentificacaoService.verificarBloqueioEnvelope(this.idConcurso).subscribe(
        res => this.envelopeBloqueado = res.bloqueado
      )
      this.reporteIdentificacaoService.listarVisiveis(this.idConcurso).subscribe(
        data => this.reportesVisiveis = data
      )
      this.comissaoJulgadorService.listarProvasExaminador(this.idConcurso).subscribe(
        data => this.isPresidente = data.some((p: any) => p.papelExaminador === 'PRESIDENTE')
      )
    }
  }

  ngOnDestroy(): void {
    this.paramSubscription.unsubscribe()
    this.querySubscription.unsubscribe()
  }

  @HostListener('document:visibilitychange')
  handleVisibilityChange() {
    if (document.visibilityState === 'visible') {
      this.getData()
    }
  }

  public getData(): void {
    this.comissaoJulgadorService
      .listarNotas(this.idConcurso, this.tipoProva)
      .subscribe({
        next: (data) => {
          this.dataSource = new MatTableDataSource(data);
          this.dataSource.sort = this.sort;
          if (data.length > 0 && data[0].comissaoNotaEnvelopeId != null) {
            this.envelope = {
              id: data[0].comissaoNotaEnvelopeId,
              dataEnvio: data[0].dataEnvio,
              enviadoPor: data[0].enviadoPor
            };
          }
        },
        error: (err) => {
          console.error('Erro ao listar notas:', err);
          this.unespCoreMessageService.showMessageError(
            err?.error?.message ?? 'Erro ao carregar as notas.'
          );
        }
      });
  }

  onNotaChange(inscricaoId: number, nota: number): void {
    this.notaSubject.next({ inscricaoId, nota });
  }

  salvarNota(row: ComissaoNotaProvaInscricaoDTO): void {
    if (row.notaAnterior == row.nota)
      return;

    if (row.nota === null || row.nota === undefined || isNaN(row.nota)) {
      this.unespCoreMessageService.showMessageError('Nota inválida.');

      row.nota = null;
      return;
    }
  
    // Verifica se está entre 0 e 10
    if (row.nota < 0 || row.nota > 10) {
      this.unespCoreMessageService.showMessageError('A nota deve estar entre 0 e 10.');
      row.nota = row.notaAnterior ?? null;        
      return;
    }
  
    // Verifica se possui mais de 2 casas decimais
    const casasDecimais = row.nota.toString().split('.')[1];
    if (casasDecimais && casasDecimais.length > 2) {
      row.nota = row.notaAnterior ?? null;  
      this.unespCoreMessageService.showMessageError('A nota deve ter no máximo 2 casas decimais.');      
      return;
    }
  
    if (this.recursoId) {
      this.salvarNotaRecurso(row);
    } else {
      this.salvarNotaNormal(row);
    }

    this.unespCoreMessageService.showMessageSuccess('Nota salva')
  }

  private salvarNotaNormal(row: ComissaoNotaProvaInscricaoDTO): void {
    const request: ComissaoNotaProvaInscricaoRequest = {
      inscricaoId: row.inscricaoId,
      concursoId: this.idConcurso,
      tipoProva: this.tipoProva,
      nota: row.nota!
    };
  
    console.log("Nota anterior antes 2: " + row.notaAnterior);
    this.comissaoJulgadorService.registrarNota(request).subscribe({
      next: (resposta) => {
        let tmp = this.dataSource.data;
        const idx = tmp.indexOf(row);
        tmp[idx].nota = row.nota;
        this.dataSource.data = [...tmp];
      },
      error: (erro) => {
        console.log("Nota anterior " + row.notaAnterior);
        row.nota = row.notaAnterior ?? null;            
        this.unespCoreMessageService.showMessageError(erro.error.message)
      }
    });
  }

  private salvarNotaRecurso(row: ComissaoNotaProvaInscricaoDTO): void {
    const notasAlteradas = this.dataSource.data
      .filter(r => r.nota !== null && r.nota !== r.notaAnterior)
      .map(r => ({
        comissaoJulgadoraId: r.comissaoJulgadoraId,
        tipoProva: this.tipoProva,
        nota: r.nota!
      }));

    const confirmDialog = new ConfirmDialogModel(
      'Confirmar retificação de notas',
      'As notas serão alteradas e a classificação será recalculada. Deseja continuar?'
    );

    const dialogRef = this.dialog.open(ConfirmDialogComponent, { data: confirmDialog });

    dialogRef.afterClosed().subscribe(res => {
      if (!res) {
        row.nota = row.notaAnterior ?? null;
        return;
      }

      this.comissaoJulgadorService.atualizarNotasRecurso(this.recursoId!, notasAlteradas).subscribe({
        next: (response: any) => {
          let tmp = this.dataSource.data;
          const idx = tmp.indexOf(row);
          tmp[idx].nota = row.nota;
          tmp[idx].notaAnterior = row.nota;
          this.dataSource.data = [...tmp];

          if (response?.todosRecursosConcluidos) {
            this.unespCoreMessageService.showMessageSuccess('Nota salva. Todos os recursos foram concluídos. Redirecionando para classificação...');
            this.router.navigate(['/comissao-julgadora/resultado/classificacao', this.idConcurso]);
          } else {
            this.unespCoreMessageService.showMessageSuccess('Nota salva. Redirecionando para recursos pendentes...');
            this.router.navigate(['/comissao/recursos-pendentes']);
          }
        },
        error: (erro) => {
          row.nota = row.notaAnterior ?? null;
          this.unespCoreMessageService.showMessageError(erro.error?.message || 'Erro ao atualizar nota')
        }
      });
    });
  }

  incluirNoEnvelope(){
    const notas = this.dataSource.data;

    if (notas.length === 0) {
      this.unespCoreMessageService.showMessageError('Não há inscrições para enviar.');
      return;
    }

    const notasInvalidas = notas.filter(n => n.nota === null || n.nota === undefined || isNaN(n.nota));
    if (notasInvalidas.length > 0) {
      this.unespCoreMessageService.showMessageError('Todas as notas devem estar preenchidas corretamente.');
      return;
    }

    const confirmDialog = new ConfirmDialogModel(
      'Atenção',
      'Ao enviar o envelope, as notas desta prova não poderão mais ser alteradas. Deseja continuar?'
    );

    const dialogRef = this.dialog.open(ConfirmDialogComponent, { data: confirmDialog });

    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        const request: EnviarNotasEnvelopeDTO = {
          concursoId: this.idConcurso,
          tipoProva: this.tipoProva
        };

        this.comissaoJulgadorService.enviarEnvelope(request).subscribe({
          next: (response) => {
            this.unespCoreMessageService.showMessageSuccess('Envelope de notas enviado com sucesso.');
            
            if (response.ultimoEnvelopeDaFase) {
              this.comissaoJulgadorService.calcularMediaExaminador(this.idConcurso).subscribe({
                next: () => {
                  this.router.navigate([`/comissao-julgadora/classificacao`, this.idConcurso]);
                },
                error: (erro) => {
                  this.unespCoreMessageService.showMessageError(
                    erro?.error?.message ?? 'Erro ao calcular média dos examinadores.'
                  );
                  this.router.navigate([`/comissao-julgadora/listar-provas`, this.idConcurso]);
                }
              });
            } else {
              this.router.navigate([`/comissao-julgadora/listar-provas`, this.idConcurso]);
            }
          },
          error: (erro) => {
            this.unespCoreMessageService.showMessageError(
              erro?.error?.message ?? 'Erro ao enviar o envelope.'
            );
          }
        });
      }
    });
  }

  visualizarDocumento(idInscricao: number, idArquivo: number, nomeArquivo: string) {
    const downloadPath = `inscricaodocumento/${idArquivo}`
    const titulo = `Inscrição #${idInscricao} - Prova Escrita`
    const row = this.dataSource.data.find(r => r.inscricaoId === idInscricao.toString())

    const reporte = this.getReportePorDocumentoId(idArquivo)
    const reportesParaDoc = this.reportesVisiveis.filter(r => r.inscricaoDocumentoId === idArquivo)

    this.comissaoJulgadorService.arquivoObjectURLPorPath(downloadPath, nomeArquivo).subscribe(url =>
      this.dialog.open(ArquivoViewerAvaliacaoComponent, {
        data: {
          tituloDoArquivo: titulo,
          urlDoArquivo: url,
          nota: row?.nota ?? null,
          envelopeEnviado: this.envelopeEnviado(),
          peso: this.provaPeso,
          inscricaoDocumentoId: idArquivo,
          jaReportado: !!reporte,
          temReportePendente: !!reporte && reporte.status === 'PENDENTE' && this.isPresidente,
          reporteData: reportesParaDoc,
          onNotaSave: (nota: number) => {
            if (row) {
              row.nota = nota
              this.salvarNota(row)
            }
          },
          onReporteIdentificacao: (justificativa: string) => {
            this.reporteIdentificacaoService.reportar(idArquivo, justificativa).subscribe({
              next: () => {
                this.unespCoreMessageService.showMessageSuccess('Reporte enviado com sucesso.')
                this.envelopeBloqueado = true
                this.reporteIdentificacaoService.listarVisiveis(this.idConcurso).subscribe(
                  data => this.reportesVisiveis = data
                )
              },
              error: (err) => {
                this.unespCoreMessageService.showMessageError(err?.error?.message ?? 'Erro ao registrar reporte.')
              }
            })
          },
          onDecisaoReporte: (acao: string, justificativa: string) => {
            if (acao === 'desclassificar') {
              this.reporteIdentificacaoService.confirmar(reporte.id, justificativa).subscribe({
                next: () => {
                  this.unespCoreMessageService.showMessageSuccess('Candidato desclassificado com sucesso.')
                  this.getData()
                  this.reporteIdentificacaoService.listarVisiveis(this.idConcurso).subscribe(
                    data => this.reportesVisiveis = data
                  )
                  this.reporteIdentificacaoService.verificarBloqueioEnvelope(this.idConcurso).subscribe(
                    res => this.envelopeBloqueado = res.bloqueado
                  )
                },
                error: (err) => this.unespCoreMessageService.showMessageError(err?.error?.message ?? 'Erro.')
              })
            } else {
              this.reporteIdentificacaoService.descartar(reporte.id, justificativa).subscribe({
                next: () => {
                  this.unespCoreMessageService.showMessageSuccess('Prova liberada para avaliação.')
                  this.reporteIdentificacaoService.listarVisiveis(this.idConcurso).subscribe(
                    data => this.reportesVisiveis = data
                  )
                  this.reporteIdentificacaoService.verificarBloqueioEnvelope(this.idConcurso).subscribe(
                    res => this.envelopeBloqueado = res.bloqueado
                  )
                },
                error: (err) => this.unespCoreMessageService.showMessageError(err?.error?.message ?? 'Erro.')
              })
            }
          },
        },
        width: '98vw',
        height: '96vh',
        maxWidth: '100vw',
        panelClass: 'fullheight-dialog',
      })
    )
  }

  envelopeEnviado(): boolean {
    return this.envelope != null;
  }

  todasNotasPreenchidas(): boolean {
    return this.dataSource?.data?.every(row => row.nota !== null && row.nota !== undefined);
  }

  visualizarDocumentosComplementares(inscricaoId: number, nota: number): void {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/comissao-julgadora/visualizar-complementos', inscricaoId], {
        queryParams: { nota, envelopeEnviado: this.envelopeEnviado(), idConcurso: this.idConcurso, tipoProva: this.tipoProva, peso: this.provaPeso }
      })
    );
    window.open(url, '_blank');
  }


  getReportePorDocumentoId(documentoId: number): any | undefined {
    return this.reportesVisiveis.find(r => r.inscricaoDocumentoId === documentoId && r.status === 'PENDENTE')
      || this.reportesVisiveis.find(r => r.inscricaoDocumentoId === documentoId)
  }

  getReporteStatusClass(documentoId: number): string {
    const reporte = this.getReportePorDocumentoId(documentoId)
    if (!reporte) return ''
    return reporte.status === 'PENDENTE' ? 'warn' : ''
  }

  getReporteIconStyle(documentoId: number): string {
    const reporte = this.getReportePorDocumentoId(documentoId)
    if (!reporte || reporte.status === 'PENDENTE') return ''
    return 'color: #2e7d32'
  }

  abrirHistoricoReporte(documentoId: number): void {
    const reportes = this.reportesVisiveis.filter(r => r.inscricaoDocumentoId === documentoId)
    if (!reportes.length) return
    this.dialog.open(ReporteHistoricoDialogComponent, {
      data: reportes,
      width: '60vw',
      maxWidth: '90vw',
      autoFocus: false,
    })
  }
  getDescricaoTipoProva(): string {
    switch (this.tipoProva) {
      case 'ESCRITA':
        return 'Prova Escrita';
      case 'PLANO':
        return 'Plano de Trabalho';
      case 'ESPECIFICA':
        return 'Prova Especifica';
      case 'TITULOS':
        return 'Prova de Títulos';
      case 'ARGUICAO':
        return 'Prova de Arguição';
      case 'DIDATICA':
        return 'Prova Didátca';
      default:
        return 'Tipo de prova desconhecido';
    }
  }

  toggleInstrucoes(): void {
    this.instrucoesExpandidas = !this.instrucoesExpandidas;
  }

  voltar(): void {
    this.router.navigate(['/comissao-julgadora/listar-provas', this.idConcurso]);
  }
}

